import { type ReactNode, useContext, useSyncExternalStore } from 'react';

import { useTableOutreaches } from '@apps/web/features/phoebe_chat_v3/api/outreach_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type UiContextSnapshot } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import { SELECTABLE_ID_COLUMNS } from '@apps/web/features/phoebe_chat_v3/registry/types';
import {
    type EntityDetailHandlers,
    useEntityDetailHandlers,
} from '@apps/web/features/phoebe_chat_v3/ui/entity_detail_context';
import { LiveOutreachCard } from '@apps/web/features/phoebe_chat_v3/ui/live_outreach_card';
import {
    GeneratedTable,
    type GeneratedTableSelection,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/generated_table';
import { TableSelectionContext } from '@apps/web/features/phoebe_chat_v3/ui/table_selection_context';
import { useAgentRunWorkspaceFileRows } from '@apps/web/hooks/use_agent_run_workspace_file_rows';

// Generated tables render inside chat, so pages stay small enough to read in
// place rather than scroll.
const GENERATED_TABLE_PAGE_SIZE = 15;
const EMPTY_IDS: ReadonlySet<string> = new Set();
const emptySelection = () => null;
const subscribeWithoutStore = () => () => {};

export type GeneratedTableResourceProps = Readonly<{
    readonly scope: HarnessScope;
    readonly tableOutreaches?: ReadonlyArray<
        Readonly<{ outreachId: string; sourceEventId: string }>
    >;
    readonly title: string | null;
    readonly workspacePath: string;
    readonly format: 'csv' | 'jsonl';
    readonly rowCount: number;
    readonly columns: ReadonlyArray<string>;
    readonly columnsTruncated: boolean;
    readonly sourceEventId: string;
    readonly uiContextReceipt?: Omit<UiContextSnapshot['receipt'], 'run_id'>;
    readonly selection?: GeneratedTableSelection;
    /** Id column whose rows open their record's detail in place. */
    readonly rowIdColumn?: string;
}>;

function rowOpenHandler(
    handlers: EntityDetailHandlers | null,
    rowIdColumn: string | undefined,
): ((id: string) => void) | undefined {
    if (handlers === null) {
        return undefined;
    }
    switch (rowIdColumn) {
        case 'shift_id':
            return handlers.openShift;
        case 'caregiver_id':
            return handlers.openCaregiver;
        case 'client_id':
            return handlers.openClient;
        default:
            return undefined;
    }
}

export function GeneratedTableResource(
    props: GeneratedTableResourceProps,
): ReactNode {
    const existingOutreach = props.tableOutreaches?.find(
        (outreach) => outreach.sourceEventId === props.sourceEventId,
    );
    if (existingOutreach !== undefined) {
        return (
            <LiveOutreachCard
                key={`${props.scope.organizationId}:${props.scope.mode}:${existingOutreach.outreachId}`}
                scope={props.scope}
                outreachId={existingOutreach.outreachId}
                heading="created"
            />
        );
    }
    return <GeneratedTableContent {...props} />;
}

function GeneratedTableContent({
    scope,
    title,
    workspacePath,
    format,
    rowCount,
    columns,
    columnsTruncated,
    sourceEventId,
    uiContextReceipt,
    selection,
    rowIdColumn,
}: GeneratedTableResourceProps): ReactNode {
    const selectionStore = useContext(TableSelectionContext);
    const activeSelection = useSyncExternalStore(
        selectionStore?.subscribe ?? subscribeWithoutStore,
        selectionStore?.getSnapshot ?? emptySelection,
    );
    const selectableIdColumn = SELECTABLE_ID_COLUMNS.find(
        (column) => column === selection?.idColumn,
    );
    const onOpenRow = rowOpenHandler(useEntityDetailHandlers(), rowIdColumn);
    const workspaceRows = useAgentRunWorkspaceFileRows({
        organizationId: scope.organizationId,
        runId: scope.runId,
        filePath: workspacePath,
        format,
        eventId: sourceEventId,
        mode: scope.mode,
        pageSize: GENERATED_TABLE_PAGE_SIZE,
    });
    const rows = workspaceRows.pageRows ?? workspaceRows.rows;
    const shiftIds =
        selection?.idColumn === 'shift_id'
            ? rows.flatMap((row) =>
                  typeof row['shift_id'] === 'string' ? [row['shift_id']] : [],
              )
            : [];
    const outreaches = useTableOutreaches({
        organizationId: scope.organizationId,
        mode: scope.mode,
        shiftIds,
    });
    return (
        <GeneratedTable
            key={`${scope.organizationId}:${scope.mode}:${scope.runId}:${sourceEventId}`}
            title={title ?? 'Results'}
            organizationTimeZone={scope.organizationTimeZone}
            columns={columns}
            columnsTruncated={columnsTruncated}
            rows={rows}
            rowCount={rowCount}
            pageOffset={workspaceRows.pageOffset ?? 0}
            pageSizeRows={GENERATED_TABLE_PAGE_SIZE}
            isLoading={workspaceRows.isLoading}
            isLoadingMore={workspaceRows.isLoadingMore}
            loadError={
                workspaceRows.error !== undefined &&
                workspaceRows.pageError === undefined
            }
            pageLoadError={workspaceRows.pageError !== undefined}
            hasPrevious={workspaceRows.hasPrevious ?? false}
            hasMore={workspaceRows.hasMore}
            onPrevious={workspaceRows.loadPreviousPage}
            onNext={workspaceRows.loadNextPage}
            selection={
                selection === undefined
                    ? undefined
                    : {
                          ...selection,
                          ...(selectionStore === null ||
                          selectableIdColumn === undefined
                              ? {}
                              : {
                                    selectedIds:
                                        activeSelection?.eventId ===
                                        sourceEventId
                                            ? activeSelection.ids
                                            : EMPTY_IDS,
                                    onSelectedIdsChange: (update) => {
                                        const current =
                                            selectionStore.getSnapshot();
                                        const ids =
                                            current?.eventId === sourceEventId
                                                ? current.ids
                                                : EMPTY_IDS;
                                        const nextIds =
                                            typeof update === 'function'
                                                ? update(ids)
                                                : update;
                                        // A completed action on an older table must not clear a newer pick.
                                        if (
                                            nextIds.size === 0 &&
                                            current?.eventId !== sourceEventId
                                        ) {
                                            return;
                                        }
                                        selectionStore.update(
                                            nextIds.size === 0
                                                ? null
                                                : {
                                                      eventId: sourceEventId,
                                                      idColumn:
                                                          selectableIdColumn,
                                                      ids: nextIds,
                                                      ...(uiContextReceipt ===
                                                      undefined
                                                          ? {}
                                                          : {
                                                                receipt: {
                                                                    ...uiContextReceipt,
                                                                    run_id: scope.runId,
                                                                },
                                                            }),
                                                  },
                                        );
                                    },
                                }),
                          disabled:
                              outreaches.isLoading ||
                              outreaches.error !== undefined,
                          statusMessage:
                              outreaches.error !== undefined
                                  ? 'Could not check existing outreaches. Refresh to try again.'
                                  : outreaches.isLoading
                                    ? 'Checking outreaches…'
                                    : undefined,
                          onAction: (action, ids, completeAction) =>
                              selection.onAction(action, ids, (receipt) => {
                                  completeAction(receipt);
                                  if (receipt !== undefined) {
                                      void outreaches.mutate();
                                  }
                              }),
                      }
            }
            rowOpen={
                onOpenRow === undefined || rowIdColumn === undefined
                    ? undefined
                    : { idColumn: rowIdColumn, onOpen: onOpenRow }
            }
        />
    );
}
